import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as citiesApi from './cities.api';

import type {
  CityListQuery,
  CreateCityRequest,
  SetCentralStationInput,
  UpdateCityRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: CityListQuery) => ['cities', 'list', query] as const,
  detail: (id: string) => ['cities', 'detail', id] as const,
  transport: (id: string) => ['cities', 'transport', id] as const,
  countries: ['geo', 'countries'] as const,
  states: (countryId: string) => ['geo', 'states', countryId] as const,
};

export const useCitiesList = (query: CityListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => citiesApi.listCities(query),
    staleTime: 30_000,
  });

export const useCountries = () =>
  useQuery({
    queryKey: QK.countries,
    queryFn: citiesApi.listCountries,
    // Seeded master data - rarely changes within a session.
    staleTime: 5 * 60_000,
  });

export const useStates = (countryId: string | null) =>
  useQuery({
    queryKey: QK.states(countryId ?? ''),
    queryFn: () => citiesApi.listStates(countryId!),
    enabled: !!countryId,
    staleTime: 5 * 60_000,
  });

export const useCreateCity = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCityRequest) => citiesApi.createCity(payload),
    onSuccess: (city) => {
      qc.invalidateQueries({ queryKey: ['cities', 'list'] });
      toast.success(`Added ${city.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add city'),
  });
};

export const useUpdateCity = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCityRequest }) =>
      citiesApi.updateCity(id, payload),
    onSuccess: (city) => {
      qc.invalidateQueries({ queryKey: ['cities', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(city.id) });
      toast.success('City updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update city'),
  });
};

export const useDeleteCity = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => citiesApi.deleteCity(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cities', 'list'] });
      toast.success('City deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete city'),
  });
};

export const useBulkDeleteCities = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => citiesApi.bulkDeleteCities(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cities', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete cities'),
  });
};

export const useBulkSetCitiesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      citiesApi.bulkSetCitiesStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cities', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update city status'),
  });
};

export const useCityTransport = (cityId: string | null) =>
  useQuery({
    queryKey: QK.transport(cityId ?? ''),
    queryFn: () => citiesApi.getCityTransport(cityId!),
    enabled: !!cityId,
    staleTime: 60_000,
  });

export const useSetCityCentralStation = (cityId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SetCentralStationInput) =>
      citiesApi.setCityCentralStation(cityId, payload),
    onSuccess: (data) => {
      qc.setQueryData(QK.transport(cityId), data);
      toast.success('Central station updated');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update central station'),
  });
};

export const useResyncCityTransport = (cityId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => citiesApi.resyncCityTransport(cityId),
    onSuccess: (data) => {
      qc.setQueryData(QK.transport(cityId), data);
      toast.success(`Synced: ${data.stations.length} stations, ${data.airports.length} airports`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'OpenStreetMap sync failed'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
